import type { PackageReference } from "@octopusdeploy/octopus-server-client";
import { DOCKER_IMAGE_REFERENCE_PURPOSE, GetNamedPackageReferences } from "@octopusdeploy/octopus-server-client";
import pluralize from "pluralize";
import * as React from "react";
import type { RelatedTriggersDependencies } from "~/areas/projects/components/Triggers/ProcessCallouts/CalloutReferencedPackageFeedTriggerConnections";
import type { ActionWithFeeds } from "~/components/Actions/commonActionHelpers";
import { ContainerImageReferenceList } from "~/components/Actions/packageReferences";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties, ScriptPackageReference } from "~/components/Actions/script/ScriptPackageReferenceDialog";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { ProducerSuppliedFormSection } from "~/components/form/Sections/index";
import { FormSectionHeading, Note, Summary } from "~/components/form/index";
type PackageReferenceListProps = ActionEditProps<{}, ScriptPackageProperties> & ActionWithFeeds & {
    relatedTriggersDependencies?: RelatedTriggersDependencies;
    modelDirty?: boolean;
    packageReferences: ScriptPackageReference[];
};
type DockerReferenceListFormSectionProps = Omit<PackageReferenceListProps, "packageReferences" | "additionalPackageProperties">;
export function DockerReferenceListFormSectionReadOnly(props: DockerReferenceListFormSectionProps) {
    const { expandedByDefault, packages } = props;
    const getDockerImageReferences = () => {
        return GetNamedPackageReferences(packages).filter((p) => p.Properties.Purpose === DOCKER_IMAGE_REFERENCE_PURPOSE);
    };
    const packageReferences = getDockerImageReferences();
    const packageReferenceSummary = (packageReferences: PackageReference[]) => {
        if (packageReferences.length === 0) {
            return Summary.placeholder("No additional packages referenced");
        }
        return Summary.summary(`${packageReferences.length} package ${pluralize("reference", packageReferences.length)}`);
    };
    return (<div>
            <FormSectionHeading title="Docker image references"/>
            <ProducerSuppliedFormSection title={"Referenced Images"} isExpandedByDefault={expandedByDefault} errorKey="Octopus.Action.DockerImageReferences" summary={packageReferenceSummary(packageReferences)} help={"Add docker images to be referenced by your templates at execution-time"}>
                <Note>
                    Docker images referenced by this step have their version (tag) selected at release creation. Properties relating to the images are then accessible via special package variables. Learn more about{" "}
                    <ExternalLink href="ScriptStepPackageReferences">package references</ExternalLink>.
                </Note>
                <ContainerImageReferenceList {...props} packageReferences={packageReferences}/>
            </ProducerSuppliedFormSection>
        </div>);
}
